import {
  Textarea,
  Button,
  EmptyState,
  ErrorNotice,
  IconButton,
  Modal,
  MenuAction,
  MenuDivider,
} from '@kiipu/ui';
import { ImageLightbox } from '@kiipu/ui/image-lightbox';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  ArrowLeft,
  ArrowSquareOut,
  ArrowsOut,
  ArrowsIn,
  CaretLeft,
  CaretRight,
  FolderSimple,
  Star,
  BookOpen,
  Info,
  NotePencil,
  VideoCamera,
  ClockCounterClockwise,
  TextAa,
  ListBullets,
  MagnifyingGlass,
  Browsers,
} from '@phosphor-icons/react';

import { api, formatDate, platformName, safeHttpUrl, statusLabel, type ItemDetail } from '../api';
import { useSession, useToast } from '../context';
import { ItemMenu, type ItemActions } from './collection';
import { SiteIcon } from './site-icon';
import { captureFailureNotice } from '@kiipu/app-protocol';
import {
  ReaderOutline,
  ReaderTypography,
  RenameTitleDialog,
  readerHeadings,
  useCurrentHeading,
  useReaderPreferences,
} from './reader-experience';
import { useReaderPosition } from '../hooks/reader-position';
import {
  canMutate,
  desktopBridge,
  isDesktop,
  platformState,
  privateMediaUrl,
  usePlatform,
} from '../lib/client-platform';
import { desktopDraftSuffix, useDesktopDirty } from '../lib/desktop-dirty-state';
import { readerCommands, useDesktopCommands } from '../lib/desktop-commands';
import { IntegrationSaveDialog } from './integrations';

function savedHtml(item: ItemDetail): string {
  if (!item.snapshot) return '';
  const clean = DOMPurify.sanitize(item.snapshot.document.html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      'style',
      'form',
      'input',
      'button',
      'textarea',
      'select',
      'iframe',
      'video',
      'audio',
      'source',
      'object',
      'embed',
      'svg',
      'math',
    ],
    FORBID_ATTR: ['style', 'srcset', 'sizes', 'id', 'name'],
  });
  const document = new DOMParser().parseFromString(clean, 'text/html');
  for (const link of document.querySelectorAll('a')) {
    const href = safeHttpUrl(link.getAttribute('href'));
    if (href) {
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    } else link.removeAttribute('href');
  }
  const imageOccurrences = new Map<string, number>();
  for (const image of document.querySelectorAll('img')) {
    const original = image.getAttribute('src');
    const inputAsset = item.snapshot.document.assets.find(
      (asset) =>
        asset.url === original ||
        asset.id === image.dataset.kiipuAsset ||
        asset.id === image.dataset.assetId,
    );
    const saved = item.snapshot.assets.find(
      (asset) => asset.sourceId === inputAsset?.id || asset.url === original,
    );
    const src = privateMediaUrl(saved?.url);
    image.removeAttribute('data-gallery');
    image.removeAttribute('data-gallery-id');
    if (src) {
      image.src = src;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.dataset.gallery = 'true';
      const identity = saved?.sourceId ?? inputAsset?.id ?? src;
      const occurrence = imageOccurrences.get(identity) ?? 0;
      imageOccurrences.set(identity, occurrence + 1);
      image.dataset.galleryId = `${identity}:${occurrence}`;
      image.tabIndex = 0;
      image.setAttribute('role', 'button');
      image.setAttribute('aria-label', `查看图片：${image.alt || '已保存的图片'}`);
    } else {
      image.removeAttribute('src');
      image.alt = image.alt || '图片尚未保存';
      image.classList.add('unavailable-image');
    }
  }
  for (const entry of document.querySelectorAll('[data-reader-anchor]'))
    entry.removeAttribute('data-reader-anchor');
  document
    .querySelectorAll<HTMLElement>('p,h1,h2,h3,h4,h5,h6,blockquote,pre,ul,ol,figure,table')
    .forEach((entry, index) => {
      entry.dataset.readerAnchor = `block-${index}`;
    });
  document.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6').forEach((heading, index) => {
    heading.id = `kiipu-heading-${index}`;
    heading.tabIndex = -1;
  });
  return document.body.innerHTML;
}
type NoteDraft = { note: string; baseNote: string; version: number };
function NoteEditor({ item }: { item: ItemDetail }) {
  const userId = useSession().data?.user?.id || '';
  const draftKey = `kiipu.note-draft.v1:${userId}:${item.id}${desktopDraftSuffix()}`;
  usePlatform();
  const [draft, setDraft] = useState<NoteDraft>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || 'null');
      if (
        saved &&
        typeof saved.note === 'string' &&
        typeof saved.baseNote === 'string' &&
        Number.isInteger(saved.version)
      )
        return saved;
    } catch {}
    return { note: item.note, baseNote: item.note, version: item.noteVersion };
  });
  const [storageFailed, setStorageFailed] = useState(false);
  const [expanded, setExpanded] = useState(Boolean(draft.note));
  const client = useQueryClient();
  const toast = useToast();
  const dirty = draft.note !== draft.baseNote;
  useDesktopDirty(`note:${item.id}`, dirty);
  const conflict = dirty && draft.version !== item.noteVersion;
  const mutation = useMutation({
    mutationFn: (version: number) =>
      api.patch(item.id, {
        note: draft.note,
        expectedNoteVersion: version,
        mutationId: crypto.randomUUID(),
      }),
    onSuccess: (result) => {
      setDraft({ note: result.note, baseNote: result.note, version: result.noteVersion });
      try {
        localStorage.removeItem(draftKey);
      } catch {}
      void client.invalidateQueries({ queryKey: ['item', item.id] });
      void client.invalidateQueries({ queryKey: ['items'] });
      toast('备注已保存');
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: ['item', item.id] });
    },
  });
  useEffect(() => {
    if (!dirty && item.noteVersion > draft.version) {
      setDraft({ note: item.note, baseNote: item.note, version: item.noteVersion });
    }
  }, [item.note, item.noteVersion, dirty, draft.version]);
  function edit(note: string) {
    const value = { ...draft, note };
    setDraft(value);
    try {
      if (note === value.baseNote) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, JSON.stringify(value));
      setStorageFailed(false);
    } catch {
      setStorageFailed(true);
    }
  }
  useEffect(() => {
    if (!dirty || isDesktop()) return;
    const prevent = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', prevent);
    return () => window.removeEventListener('beforeunload', prevent);
  }, [dirty]);
  return (
    <section className="reader-note">
      <button
        className="note-heading"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <NotePencil size={17} />
        <span>我的备注</span>
        {!expanded ? (
          <span className="subtle">{dirty ? '有未同步草稿' : '为这条内容留下想法'}</span>
        ) : null}
      </button>
      {expanded ? (
        <div className="stack">
          <Textarea
            aria-label="我的备注"
            value={draft.note}
            maxLength={20000}
            disabled={mutation.isPending || !canMutate()}
            placeholder="为什么保存它？记下一点想法…"
            onChange={(event) => edit(event.target.value)}
          />
          <ErrorNotice error={mutation.error} />
          {dirty ? (
            <p className="field-hint" role="status">
              {storageFailed
                ? '浏览器未能保留草稿，请保存备注后再离开。'
                : '草稿已保留在当前浏览器，关闭后仍可继续编辑；保存备注后才会同步到其他设备。'}
            </p>
          ) : null}
          {conflict ? (
            <div className="note-conflict">
              <p>另一台设备已修改备注。请核对最新版本后选择如何处理。</p>
              <pre>{item.note || '（服务器备注为空）'}</pre>
              <div className="row">
                <Button
                  onClick={() => {
                    setDraft({ note: item.note, baseNote: item.note, version: item.noteVersion });
                    try {
                      localStorage.removeItem(draftKey);
                    } catch {}
                    mutation.reset();
                  }}
                >
                  采用服务器版本
                </Button>
                <Button
                  disabled={!canMutate()}
                  busy={mutation.isPending}
                  onClick={() => mutation.mutate(item.noteVersion)}
                >
                  用草稿替换最新版本
                </Button>
              </div>
            </div>
          ) : null}
          <div className="row between">
            <span className="field-hint">{draft.note.length.toLocaleString()} / 20,000</span>
            <Button
              busy={mutation.isPending}
              disabled={!dirty || conflict || !canMutate()}
              onClick={() => mutation.mutate(draft.version)}
            >
              保存备注
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
export function Reader({
  id,
  onClose,
  actions,
  previous,
  next,
  focus,
  onToggleFocus,
  backLabel = '返回内容库',
  canFocus = true,
}: {
  id: string;
  onClose: () => void;
  actions: ItemActions;
  previous?: string;
  next?: string;
  focus: boolean;
  onToggleFocus: () => void;
  backLabel?: string;
  canFocus?: boolean;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [snapshotId, setSnapshotId] = useState<string | undefined>(undefined);
  const [titleOpen, setTitleOpen] = useState(false);
  const [typographyOpen, setTypographyOpen] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const paneRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const userId = useSession().data?.user?.id || '';
  const typography = useReaderPreferences(userId);
  usePlatform();
  const [find, setFind] = useState<string | null>(null);
  useLayoutEffect(() => {
    setSnapshotId(undefined);
    setHistoryOpen(false);
    setIntegrationOpen(false);
    setTitleOpen(false);
    setImage(null);
  }, [id, userId]);
  const history = useQuery({
    queryKey: ['snapshots', id, userId],
    queryFn: () => api.snapshots(id),
    enabled: historyOpen && Boolean(userId),
  });
  const query = useQuery({
    queryKey: ['item', id, snapshotId, userId],
    queryFn: () => api.item(id, snapshotId),
    enabled: Boolean(userId),
    refetchInterval: (query) =>
      ['queued', 'running'].includes(query.state.data?.status || '') ? 3000 : false,
  });
  const item = query.data;
  useDesktopCommands(
    readerCommands,
    (value) => {
      if (value === 'favorite' && item && canMutate()) actions.favorite(item);
      if (value === 'find-in-content') setFind('');
      if (value === 'refresh') void query.refetch();
    },
    Boolean(item),
  );
  const accessNotice =
    item?.capture?.status === 'failed' && item.capture.mode === 'remote'
      ? captureFailureNotice(item.capture.errorCode, item.platform)
      : null;
  const html = useMemo(() => (item ? savedHtml(item) : ''), [item]);
  const headings = useMemo(() => readerHeadings(html), [html]);
  const documentKey = `${id}:${item?.snapshot?.id || 'pending'}`;
  const position = useReaderPosition({
    userId,
    itemId: id,
    snapshotId: item?.snapshot?.id,
    historical: Boolean(snapshotId),
    scrollRef,
    contentRef,
    ready: query.isSuccess && Boolean(item?.snapshot),
  });
  const activeHeading = useCurrentHeading(scrollRef, contentRef, headings, documentKey);
  function goToHeading(headingId: string) {
    const scroller = scrollRef.current;
    const heading = contentRef.current?.querySelector<HTMLElement>(`#${headingId}`);
    if (!scroller || !heading) return;
    scroller.scrollTo({
      top:
        scroller.scrollTop +
        heading.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        24,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    heading.focus({ preventScroll: true });
  }
  const [image, setImage] = useState<string | null>(null);
  const imageTrigger = useRef<HTMLImageElement | null>(null);
  const gallery = useMemo(
    () =>
      [
        ...new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelectorAll<HTMLImageElement>('img[data-gallery]'),
      ].map((entry) => ({
        id: entry.dataset.galleryId!,
        src: entry.src,
        alt: entry.alt || '已保存的原图',
      })),
    [html],
  );
  const imageIndex = gallery.findIndex((entry) => entry.id === image);

  return (
    <section
      ref={paneRef}
      className="reader-pane reader-experience"
      aria-label="阅读内容"
      data-reader-size={typography.preferences.size}
      data-reader-width={typography.preferences.width}
    >
      <header className="reader-toolbar">
        <div className="row">
          <IconButton label={backLabel} onClick={onClose} className="reader-back">
            <ArrowLeft size={17} />
          </IconButton>
          <IconButton
            label="上一条"
            disabled={!previous}
            onClick={() => previous && actions.open(previous)}
          >
            <CaretLeft size={17} />
          </IconButton>
          <IconButton label="下一条" disabled={!next} onClick={() => next && actions.open(next)}>
            <CaretRight size={17} />
          </IconButton>
        </div>
        <div className="row">
          <ReaderOutline
            headings={headings}
            active={activeHeading}
            onChoose={goToHeading}
            dialog={isDesktop() ? { open: outlineOpen, onOpenChange: setOutlineOpen } : undefined}
          />
          <ReaderTypography
            dialog={
              isDesktop() ? { open: typographyOpen, onOpenChange: setTypographyOpen } : undefined
            }
            preferences={typography.preferences}
            onChange={(value) => {
              position.preservePosition();
              typography.change(value);
            }}
            storageFailed={typography.storageFailed}
          />
          {item ? (
            <>
              <IconButton
                label={item.favorite ? '取消收藏' : '加入收藏'}
                disabled={!canMutate()}
                aria-pressed={item.favorite}
                className={item.favorite ? 'favorite' : ''}
                onClick={() => actions.favorite(item)}
              >
                <Star size={17} weight={item.favorite ? 'fill' : 'regular'} />
              </IconButton>
              <IconButton
                disabled={!canMutate()}
                label="整理内容"
                onClick={() => actions.organize(item)}
              >
                <FolderSimple size={17} />
              </IconButton>
              <span className="toolbar-divider" />
              <a
                className="icon-button reader-secondary-action"
                href={safeHttpUrl(item.url)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="打开原文"
                title="打开原文"
              >
                <ArrowSquareOut size={17} />
              </a>
              {!isDesktop() ? (
                <IconButton label="保存历史" onClick={() => setHistoryOpen(true)}>
                  <ClockCounterClockwise size={17} />
                </IconButton>
              ) : null}
              <ItemMenu
                item={item}
                actions={actions}
                onRename={() => {
                  position.preservePosition();
                  setTitleOpen(true);
                }}
              >
                {!isDesktop() && !item.deletedAt ? (
                  <MenuAction onClick={() => setIntegrationOpen(true)}>
                    <ArrowSquareOut size={15} />
                    保存到应用
                  </MenuAction>
                ) : null}
                {isDesktop() ? (
                  <>
                    <MenuDivider />
                    <MenuAction onClick={() => setTypographyOpen(true)}>
                      <TextAa size={15} />
                      阅读设置
                    </MenuAction>
                    <MenuAction disabled={headings.length < 2} onClick={() => setOutlineOpen(true)}>
                      <ListBullets size={15} />
                      文章目录
                    </MenuAction>
                    <MenuAction onClick={() => setHistoryOpen(true)}>
                      <ClockCounterClockwise size={15} />
                      保存历史
                    </MenuAction>
                    <MenuAction onClick={() => setFind('')}>
                      <MagnifyingGlass size={15} />
                      在正文中查找
                    </MenuAction>
                    {platformState()?.windowKind === 'main' ? (
                      <>
                        <MenuAction
                          onClick={() => {
                            void desktopBridge()?.openReader(id);
                          }}
                        >
                          <Browsers size={15} />
                          在独立窗口阅读
                        </MenuAction>
                        {canFocus ? (
                          <MenuAction onClick={onToggleFocus}>
                            {focus ? <ArrowsIn size={15} /> : <ArrowsOut size={15} />}
                            {focus ? '恢复分栏' : '专注阅读'}
                          </MenuAction>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
              </ItemMenu>
              {integrationOpen ? (
                <IntegrationSaveDialog
                  key={item.id}
                  itemId={item.id}
                  open={integrationOpen}
                  onOpenChange={setIntegrationOpen}
                />
              ) : null}
            </>
          ) : null}
          {!isDesktop() ? (
            <IconButton
              label={focus ? '恢复分栏' : '专注阅读'}
              className="focus-toggle"
              onClick={onToggleFocus}
            >
              {focus ? <ArrowsIn size={17} /> : <ArrowsOut size={17} />}
            </IconButton>
          ) : null}
        </div>
      </header>
      {find !== null ? (
        <form
          className="desktop-find"
          onSubmit={(event) => {
            event.preventDefault();
            (window as unknown as { find?: (text: string) => boolean }).find?.(find);
          }}
        >
          <input
            autoFocus
            aria-label="在正文中查找"
            value={find}
            onChange={(event) => setFind(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setFind(null);
            }}
          />
          <Button type="submit">查找</Button>
          <Button onClick={() => setFind(null)}>关闭</Button>
        </form>
      ) : null}
      {position.notice || position.storageFailed ? (
        <div className="reader-position-notice" role="status">
          {position.notice === 'restored' ? (
            <>
              <span>已恢复上次阅读位置</span>
              <button type="button" onClick={position.startOver}>
                从头阅读
              </button>
            </>
          ) : position.notice === 'new-snapshot' ? (
            <span>内容已有新快照，已从当前版本开头显示。</span>
          ) : null}
          {position.storageFailed ? <span>浏览器无法保留阅读位置，本次仍可正常阅读。</span> : null}
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className="reader-scroll"
        key={documentKey}
        tabIndex={0}
        aria-label="阅读正文"
      >
        {snapshotId ? (
          <div className="snapshot-banner">
            <span>正在查看历史快照</span>
            <button onClick={() => setSnapshotId(undefined)}>返回当前版本</button>
          </div>
        ) : null}
        {query.isPending ? (
          <div className="reader-skeleton" role="status" aria-label="正在加载正文">
            <div className="skeleton" />
            <div className="skeleton" />
            {Array.from({ length: 10 }, (_, i) => (
              <div className="skeleton" key={i} />
            ))}
          </div>
        ) : query.isError ? (
          <div className="reader-padding">
            <ErrorNotice
              error={query.error}
              retry={() => {
                void query.refetch();
              }}
            />
          </div>
        ) : item ? (
          <article ref={contentRef} className="reader-document">
            <div className="reader-source">
              <SiteIcon item={item} />
              <span>{item.siteName || platformName(item.platform)}</span>
              <span className="subtle">·</span>
              <span>{item.author || new URL(item.url).hostname}</span>
            </div>
            <h1>{item.title || '未命名内容'}</h1>
            <div className="reader-metadata">
              <span>保存于 {formatDate(item.createdAt)}</span>
              <span>{item.kind === 'video' ? '视频' : item.kind === 'post' ? '笔记' : '文章'}</span>
              {item.snapshot ? <span>快照 {formatDate(item.snapshot.createdAt)}</span> : null}
            </div>
            {item.status !== 'completed' || item.quality !== 'complete' ? (
              <div className="capture-notice">
                <Info size={17} />
                <div>
                  <strong>{statusLabel(item)}</strong>
                  <p>
                    {accessNotice
                      ? '已保留链接与已有内容，整理信息不受影响。'
                      : item.status === 'queued' || item.status === 'running'
                        ? '链接已经保留，正文处理完成后会自动出现在这里。'
                        : item.quality === 'link_only'
                          ? '已保留原始链接。部分站点需要在浏览器中打开页面，再使用扩展补全正文。'
                          : '当前保存的内容仍可阅读。可以重新获取，或使用浏览器扩展补全。'}
                  </p>
                  {accessNotice ? (
                    <>
                      <p>{accessNotice.reason}</p>
                      <p>{accessNotice.guidance}</p>
                      <p>
                        <a href={safeHttpUrl(item.url)} target="_blank" rel="noopener noreferrer">
                          在 Chrome 中打开原文
                        </a>
                        {' · '}
                        <a href="/help#extension">扩展本地保存说明</a>
                      </p>
                    </>
                  ) : null}
                  {item.warnings.map((warning) => (
                    <p key={warning.code}>{warning.message}</p>
                  ))}
                </div>
              </div>
            ) : null}
            {item.kind === 'video' ? (
              <div className="video-preview">
                {privateMediaUrl(item.coverUrl) ? (
                  <img src={privateMediaUrl(item.coverUrl)} alt="视频封面" />
                ) : (
                  <VideoCamera size={36} />
                )}
                <a href={safeHttpUrl(item.url)} target="_blank" rel="noopener noreferrer">
                  <VideoCamera size={16} />在 {platformName(item.platform)} 观看
                  <ArrowSquareOut size={14} />
                </a>
                <p>已保存视频说明、封面与原始链接。</p>
              </div>
            ) : null}
            {html ? (
              <div
                className="reader-body"
                onClick={(event) => {
                  const target = event.target;
                  if (target instanceof HTMLImageElement && target.dataset.gallery) {
                    event.preventDefault();
                    imageTrigger.current = target;
                    setImage(target.dataset.galleryId ?? null);
                  }
                }}
                onKeyDown={(event) => {
                  const target = event.target;
                  if (
                    (event.key === 'Enter' || event.key === ' ') &&
                    target instanceof HTMLImageElement &&
                    target.dataset.gallery
                  ) {
                    event.preventDefault();
                    imageTrigger.current = target;
                    setImage(target.dataset.galleryId ?? null);
                  }
                }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : item.snapshot?.document.text ? (
              <div className="reader-body plain-text" data-reader-anchor="block-0">
                {item.snapshot.document.text}
              </div>
            ) : (
              <div className="reader-no-body">
                <BookOpen size={28} />
                <p>
                  {item.status === 'queued' || item.status === 'running'
                    ? '正文正在路上'
                    : '暂时没有可阅读的正文'}
                </p>
                <a href={safeHttpUrl(item.url)} target="_blank" rel="noopener noreferrer">
                  打开原始链接 <ArrowSquareOut size={14} />
                </a>
              </div>
            )}
            <NoteEditor item={item} key={item.id} />
            <footer className="reader-end">
              <span />
              <BookOpen size={16} />
              <span />
              <p>内容已保存在你的 Kiipu</p>
            </footer>
          </article>
        ) : null}
      </div>
      {titleOpen && item ? (
        <RenameTitleDialog key={item.id} item={item} onClose={() => setTitleOpen(false)} />
      ) : null}
      <Modal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        title="保存历史"
        description="重新获取内容会创建新快照，旧版本仍可阅读。"
      >
        <ErrorNotice error={history.error} />
        {history.isPending ? (
          <p className="muted" role="status">
            正在加载保存历史…
          </p>
        ) : null}
        <div className="snapshot-list">
          {history.data?.items.map((snapshot) => (
            <button
              key={snapshot.id}
              onClick={() => {
                setSnapshotId(snapshot.id === item?.snapshotId ? undefined : snapshot.id);
                setHistoryOpen(false);
              }}
            >
              <ClockCounterClockwise size={16} />
              <span>{new Date(snapshot.createdAt).toLocaleString('zh-CN')}</span>
              {snapshot.id === item?.snapshotId ? <span className="tag">当前版本</span> : null}
            </button>
          ))}
          {history.data?.items.length === 0 ? (
            <p className="muted">正文保存完成后，快照会显示在这里。</p>
          ) : null}
        </div>
      </Modal>
      <ImageLightbox
        items={gallery}
        open={image !== null}
        initialIndex={Math.max(0, imageIndex)}
        loop={false}
        finalFocus={() => {
          const trigger = imageTrigger.current;
          if (trigger?.isConnected) return trigger;
          const replacement = [
            ...(contentRef.current?.querySelectorAll<HTMLImageElement>('img[data-gallery-id]') ??
              []),
          ].find((entry) => entry.dataset.galleryId === trigger?.dataset.galleryId);
          return replacement ?? paneRef.current?.querySelector<HTMLButtonElement>('.reader-back');
        }}
        onOpenChange={(open) => {
          if (!open) setImage(null);
        }}
      />
    </section>
  );
}
